import { yupResolver } from '@hookform/resolvers/yup';
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useQuery } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import type { AxiosError } from 'axios';
import * as Yup from 'yup';

// components
import { Box, Typography as MuiTypography, styled } from '@mui/material';

import Showable from '../../Showable';
import { LoadingButton } from '../../Buttons/LoadingButton';
import { PasswordField } from '../../inputs/text-fields/passwords/PasswordField';

import NextPublicWrapper from '../NextPublicWrapper';
import { PasswordConstraintsList } from './PasswordContraintsList';

import { withShowable } from '../../../console/_global/lib/withShowable';

// other
import {
  checkUserInvitedValToken,
  postUserInvitedValToken,
  validatePassword,
} from '../../../api/SignupService';

import { usePasswordConstraints } from '../../hooks/auth/usePasswordConstraints';
import type { ValidatePasswordDto } from '../../../api/auth/auth.dto';
import { cacheAuthTokens } from '../../../utils/next/auth.utils';

interface FormValues {
  password: string;
  confirmedPassword: string;
}

interface QueryParams {
  token: string;
}

const Typography = withShowable(MuiTypography);

function NewUserInvitationConfirmation() {
  const history = useHistory();
  const { token } = useParams<QueryParams>();

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
  });

  function onTokenValidationError() {
    history.push('/login');
  }

  const { error, isError, isInitialLoading }: any =
    useInvitationTokenValidation(token, onTokenValidationError);

  const constraints = usePasswordConstraints();

  function onSubmit(values: FormValues) {
    return postUserInvitedValToken(token, values).then((resp: any) => {
      cacheAuthTokens(resp.data.accessToken, resp.data.refreshToken);
      history.replace('/loading');
    });
  }

  function handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    methods.setValue('password', value);

    handleValidatePassword(value);
  }

  function handleConfirmPasswordChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const { value } = event.target;
    methods.setValue('confirmedPassword', value);
  }

  async function handleValidatePassword(nextValue: string) {
    try {
      const remoteErrors: any = await validatePassword(
        {
          token,
          isResetToken: true,
        },
        {
          password: nextValue,
        }
      );

      handleValidatePasswordError(remoteErrors);
    } catch (error: any) {
      if (error.response.data) {
        handleValidatePasswordError(error.response.data);
      } else {
        throw error;
      }
    }
  }

  function handleValidatePasswordError({ errors }: ValidatePasswordDto) {
    constraints.setPasswordErrors(errors);
  }

  return (
    <NextPublicWrapper
      MarketingSlot={
        <PasswordRequirements
          constraints={constraints.data}
          initial={constraints.initial}
        />
      }
    >
      <Showable show={!isInitialLoading && isError}>
        <Typography paragraph>{error && (error?.message as string)}</Typography>
        <Typography>
          To get a new link, please contact Cowbell Support Team at
          <a href="mailto:support@cowbellcyber.ai">support@cowbellcyber.ai</a>.
        </Typography>
      </Showable>
      <Showable show={!isError}>
        <Typography>Thank you for confirming your email address.</Typography>
        <Typography paragraph gutterBottom>
          Please set your password.
        </Typography>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Box mb={2}>
              <PasswordField
                // @ts-expect-error TS(2322): Type '{ id: string; label: string; required: true;... Remove this comment to see the full error message
                id="password"
                label="Password"
                required
                onChange={handlePasswordChange}
              />
            </Box>

            <PasswordField
              // @ts-expect-error TS(2322): Type '{ id: string; label: string; required: true;... Remove this comment to see the full error message
              id="confirmedPassword"
              label="Confirm Password"
              required
              onChange={handleConfirmPasswordChange}
              error={methods.formState.errors.confirmedPassword}
            />
            <ErrorMessage name="confirmedPassword" />

            <Box mt={2}>
              <LoadingButton
                styleName="ctaButton"
                type="submit"
                loading={methods.formState.isSubmitting}
                disabled={methods.formState.isSubmitting}
              >
                Save Password
              </LoadingButton>
            </Box>
          </form>
        </FormProvider>
      </Showable>
    </NextPublicWrapper>
  );
}

export default NewUserInvitationConfirmation;

const validationSchema = Yup.object().shape({
  password: Yup.string().required(),
  confirmedPassword: Yup.string().oneOf(
    [Yup.ref('password'), null],
    'Passwords must match'
  ),
});

function useInvitationTokenValidation(token: string, onError: () => void) {
  return useQuery(
    ['user', 'invite', 'token'],
    () =>
      checkUserInvitedValToken(token)
        .then(() => true)
        .catch((error: AxiosError) => {
          if (
            error.response?.status === 400 ||
            error.response?.status === 404
          ) {
            throw new Error(
              `The reset link you're using has expired and you will need to get a new one to continue resetting your password`
            );
          }

          if (error.response?.status === 401) {
            return onError();
          }

          throw new Error(
            `An unexpected error has occurred ${error.response?.status}`
          );
        }),
    {
      retry: false,
    }
  );
}

function PasswordRequirements({ constraints, initial }: any) {
  return (
    <RequirementsContainer className="direction-column justify-center">
      <Typography className="canon-text" gutterBottom>
        We take your cyber security seriously
      </Typography>
      <Typography className="tertia-text" gutterBottom>
        We highly recommend using a secure password manager as an additional
        layer of security and convienence.
      </Typography>
      <RequirementsCard>
        <PasswordConstraintsList constraints={constraints} initial={initial} />
      </RequirementsCard>
      <Box mt={3}>
        <Typography className="normal-text text-center" gutterBottom>
          If you are have trouble resetting your password please reach out to{' '}
          <a href="mailto:support@cowbellcyber.ai">support@cowbellcyber.ai</a>
        </Typography>
      </Box>
    </RequirementsContainer>
  );
}

const RequirementsContainer = styled(Box)({
  padding: '2rem',
  zIndex: 1,
});

const RequirementsCard = styled(Box)(({ theme }) => ({
  marginTop: '1rem',
  background: '#26262e',
  border: '1px solid #1a1a1a',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 15px 15px -5px #000',
}));
